import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import {
  Paper,
  Typography,
  Button,
  Box,
  Chip,
  Card,
  Skeleton,
  TextField,
  InputAdornment,
  Switch,
  Tooltip,
  LinearProgress,
  IconButton,
  Tabs,
  Tab,
  Divider,
  Badge,
  Alert,
  Collapse,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add,
  Cancel,
  CheckCircle,
  IntegrationInstructions,
  Pending,
  Sync,
  Warning,
  RocketLaunch,
  Search,
  Refresh,
  AccessTime,
  WifiTethering,
  WifiTetheringOff,
  TrendingUp,
  EmojiEvents,
} from "@mui/icons-material";
import {
  PieChart,
  Pie,
  Cell as PieCell,
  Tooltip as ReTooltip,
  ResponsiveContainer,
} from "recharts";
import { useNavigate } from "react-router-dom";
import CreateDeployment from "./CreateDeploymentDialog";
import { can } from "../../../auth/can";
import { useAuth } from "../../../auth/useAuth";

import { APP_API_V1_BASE } from "../../../config/api";
const API_BASE = APP_API_V1_BASE;

/* ─── Animated Counter Hook ─── */
function useCountUp(target, duration = 900) {
  const [val, setVal] = useState(0);
  const raf = useRef();
  useEffect(() => {
    if (target === 0) {
      setVal(0);
      return;
    }
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(ease * target));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);
  return val;
}

/* ─── Animated Stat Card ─── */
function StatCard({ card, total, delay = 0 }) {
  const animated = useCountUp(card.value);
  const pct = total > 0 ? Math.round((card.value / total) * 100) : 0;
  return (
    <Card
      sx={{
        p: 1.8,
        borderRadius: "14px",
        border: `1.5px solid ${card.border}`,
        boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
        background: "#fff",
        transition: "transform 0.18s, box-shadow 0.18s",
        animation: `fadeSlideUp 0.45s ease ${delay}ms both`,
        "@keyframes fadeSlideUp": {
          from: { opacity: 0, transform: "translateY(10px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 6px 20px rgba(0,0,0,0.09)",
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            background: card.bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& svg": { color: card.iconColor, fontSize: 18 },
          }}
        >
          {card.icon}
        </Box>
        <Typography
          sx={{
            fontSize: "11px",
            color: card.iconColor,
            fontWeight: 700,
            background: card.bg,
            px: 1,
            py: 0.3,
            borderRadius: "6px",
          }}
        >
          {pct}%
        </Typography>
      </Box>

      <Typography
        sx={{
          fontSize: "22px",
          fontWeight: 800,
          color: "#0F172A",
          lineHeight: 1,
        }}
      >
        {animated}
      </Typography>
      <Typography
        sx={{
          fontSize: "11px",
          color: "#94A3B8",
          fontWeight: 500,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          mt: 0.3,
          mb: 1,
        }}
      >
        {card.label}
      </Typography>

      {/* Progress bar */}
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 4,
          borderRadius: 2,
          background: "#F1F5F9",
          "& .MuiLinearProgress-bar": {
            background: card.iconColor,
            borderRadius: 2,
          },
        }}
      />
    </Card>
  );
}

/* ─── Success Rate Banner ─── */
function SuccessRateBanner({ rows, loading }) {
  const total = rows.length;
  const completed = rows.filter((r) => r.status === "COMPLETED").length;
  const failed = rows.filter((r) => r.status === "FAILED").length;
  const pending = rows.filter((r) => r.status === "PENDING").length;
  const rate = total > 0 ? Math.round((completed / total) * 100) : 0;
  const animatedRate = useCountUp(rate);

  const color = rate >= 80 ? "#22C55E" : rate >= 50 ? "#F59E0B" : "#EF4444";
  const bgGradient =
    rate >= 80
      ? "linear-gradient(135deg, #F0FDF4, #DCFCE7)"
      : rate >= 50
        ? "linear-gradient(135deg, #FFFBEB, #FEF3C7)"
        : "linear-gradient(135deg, #FEF2F2, #FEE2E2)";

  return (
    <Box
      sx={{
        background: bgGradient,
        border: `1.5px solid ${color}30`,
        borderRadius: "14px",
        p: "12px 18px",
        mb: 2,
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
        animation: "fadeSlideUp 0.4s ease both",
        "@keyframes fadeSlideUp": {
          from: { opacity: 0, transform: "translateY(-8px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: "50%",
          border: `3px solid ${color}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <EmojiEvents sx={{ color, fontSize: 20 }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 200 } }}>
        <Box display="flex" alignItems="baseline" gap={0.8}>
          <Typography
            sx={{ fontSize: "26px", fontWeight: 900, color, lineHeight: 1 }}
          >
            {loading ? <Skeleton width={50} /> : `${animatedRate}%`}
          </Typography>
          <Typography
            sx={{ fontSize: "12px", color: "#64748B", fontWeight: 600 }}
          >
            Success Rate
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={loading ? 0 : rate}
          sx={{
            mt: 0.8,
            height: 5,
            borderRadius: 3,
            background: "#E2E8F0",
            "& .MuiLinearProgress-bar": {
              background: color,
              borderRadius: 3,
              transition: "width 1s cubic-bezier(0.34, 1.56, 0.64, 1)",
            },
          }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {[
          { label: "Completed", value: completed, color: "#22C55E" },
          { label: "Failed", value: failed, color: "#EF4444" },
          { label: "Pending", value: pending, color: "#F59E0B" },
        ].map((s) => (
          <Box key={s.label} sx={{ textAlign: "center" }}>
            <Typography
              sx={{ fontSize: "16px", fontWeight: 800, color: s.color }}
            >
              {loading ? <Skeleton width={20} /> : s.value}
            </Typography>
            <Typography
              sx={{
                fontSize: "10px",
                color: "#94A3B8",
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.4px",
              }}
            >
              {s.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box display="flex" alignItems="center" gap={0.5}>
        <TrendingUp sx={{ color, fontSize: 16 }} />
        <Typography sx={{ fontSize: "11px", color, fontWeight: 700 }}>
          {rate >= 80 ? "Excellent" : rate >= 50 ? "Good" : "Needs Attention"}
        </Typography>
      </Box>
    </Box>
  );
}

/* ─── Main Component ─── */
const STATUS_TABS = [
  "ALL",
  "SCHEDULED",
  "PENDING",
  "IN_PROGRESS",
  "COMPLETED",
  "FAILED",
  "CANCELED",
];
const PIE_COLORS = ["#22C55E", "#F59E0B", "#8B5CF6", "#3B82F6", "#EF4444"];
const PIE_DARK_COLORS = ["#15803D", "#B45309", "#6D28D9", "#1D4ED8", "#B91C1C"];
const AUTO_REFRESH_INTERVAL = 30;

const getEffectiveDeploymentStatus = (deployment) => {
  const scheduledAt = deployment?.scheduled_at
    ? new Date(deployment.scheduled_at)
    : null;

  if (
    deployment?.rollout_strategy === "SCHEDULED" &&
    deployment?.status === "PENDING" &&
    scheduledAt &&
    !Number.isNaN(scheduledAt.getTime()) &&
    scheduledAt.getTime() > Date.now()
  ) {
    return "SCHEDULED";
  }

  return deployment?.status || "PENDING";
};

const formatDeploymentStatusLabel = (status) => {
  switch (status) {
    case "SCHEDULED":
      return "Scheduled";
    case "PENDING":
      return "Pending Ack";
    case "IN_PROGRESS":
      return "Dispatched";
    case "COMPLETED":
      return "Completed";
    case "FAILED":
      return "Failed";
    case "CANCELED":
      return "Canceled";
    default:
      return status || "-";
  }
};

const formatTabLabel = (status) => {
  if (status === "ALL") return "All";
  return formatDeploymentStatusLabel(status);
};

const getDeploymentGroupKey = (deployment) => {
  const deviceKey = deployment?.target_id || "unknown-device";
  const firmwareKey = deployment?.firmware_id || "unknown-firmware";
  return `${deviceKey}::${firmwareKey}`;
};

export default function FirmwareDeployments() {
  const [rows, setRows] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  // const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [tabStatus, setTabStatus] = useState("ALL");
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [countdown, setCountdown] = useState(AUTO_REFRESH_INTERVAL);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [timeAgo, setTimeAgo] = useState("");
  const [deploymentNotice, setDeploymentNotice] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});

  const timerRef = useRef(null);
  const cdRef = useRef(null);
  const navigate = useNavigate();

  const { permissions, activeOrg } = useAuth();
  const isPlatformAdmin = can(permissions, "system.manage");
  const token = localStorage.getItem("authToken");

  /* fetch */
  const fetchDeployments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${APP_API_V1_BASE}/firmware/deployments`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(activeOrg?.id ? { "X-Org-ID": activeOrg.id } : {}),
        },
      });
      setRows(res.data);
      setLastUpdated(new Date());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token, activeOrg?.id]);

  useEffect(() => {
    if (!isPlatformAdmin || !can(permissions, "firmware.view")) return;
    fetchDeployments();
  }, [fetchDeployments, permissions, isPlatformAdmin]);

  /* time ago updater */
  useEffect(() => {
    if (!lastUpdated) return;
    const update = () => {
      const diff = Math.round((Date.now() - lastUpdated.getTime()) / 1000);
      if (diff < 60) setTimeAgo(`${diff}s ago`);
      else if (diff < 3600) setTimeAgo(`${Math.floor(diff / 60)}m ago`);
      else setTimeAgo(`${Math.floor(diff / 3600)}h ago`);
    };
    update();
    const id = setInterval(update, 10000);
    return () => clearInterval(id);
  }, [lastUpdated]);

  /* auto-refresh */
  useEffect(() => {
    clearInterval(timerRef.current);
    clearInterval(cdRef.current);
    if (!autoRefresh) {
      setCountdown(AUTO_REFRESH_INTERVAL);
      return;
    }
    setCountdown(AUTO_REFRESH_INTERVAL);
    timerRef.current = setInterval(() => {
      fetchDeployments();
      setCountdown(AUTO_REFRESH_INTERVAL);
    }, AUTO_REFRESH_INTERVAL * 1000);
    cdRef.current = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => {
      clearInterval(timerRef.current);
      clearInterval(cdRef.current);
    };
  }, [autoRefresh, fetchDeployments]);

  const count = (s) =>
    rows.filter((r) => getEffectiveDeploymentStatus(r) === s).length;
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const statsCards = [
    {
      icon: <IntegrationInstructions />,
      bg: "#EFF6FF",
      iconColor: "#3B82F6",
      label: "Total",
      value: rows.length,
      border: "#BFDBFE",
    },
    {
      icon: <CheckCircle />,
      bg: "#F0FDF4",
      iconColor: "#22C55E",
      label: "Completed",
      value: count("COMPLETED"),
      border: "#BBF7D0",
    },
    {
      icon: <Pending />,
      bg: "#FFFBEB",
      iconColor: "#F59E0B",
      label: "Pending Ack",
      value: count("PENDING"),
      border: "#FDE68A",
    },
    {
      icon: <AccessTime />,
      bg: "#F5F3FF",
      iconColor: "#8B5CF6",
      label: "Scheduled",
      value: count("SCHEDULED"),
      border: "#DDD6FE",
    },
    {
      icon: <Sync />,
      bg: "#EFF6FF",
      iconColor: "#60A5FA",
      label: "Dispatched",
      value: count("IN_PROGRESS"),
      border: "#BFDBFE",
    },
    {
      icon: <Cancel />,
      bg: "#FFF1F2",
      iconColor: "#F43F5E",
      label: "Canceled",
      value: count("CANCELED"),
      border: "#FECDD3",
    },
    {
      icon: <Warning />,
      bg: "#FEF2F2",
      iconColor: "#EF4444",
      label: "Failed",
      value: count("FAILED"),
      border: "#FECACA",
    },
  ];

  const pieData = [
    { name: "Completed", value: count("COMPLETED") || 1 },
    { name: "Pending", value: count("PENDING") || 1 },
    { name: "Scheduled", value: count("SCHEDULED") || 1 },
    { name: "In Progress", value: count("IN_PROGRESS") || 1 },
    { name: "Failed", value: count("FAILED") || 1 },
  ];

  /* filtered rows */
  const filteredRows = rows.filter((r) => {
    const effectiveStatus = getEffectiveDeploymentStatus(r);
    const matchTab = tabStatus === "ALL" || effectiveStatus === tabStatus;
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      (r.firmware_id || "").toLowerCase().includes(q) ||
      (r.target_type || "").toLowerCase().includes(q) ||
      (r.rollout_strategy || "").toLowerCase().includes(q) ||
      (effectiveStatus || "").toLowerCase().includes(q) ||
      formatDeploymentStatusLabel(effectiveStatus).toLowerCase().includes(q);
    return matchTab && matchSearch;
  });

  const groupedRows = filteredRows.reduce((acc, row) => {
    const key = getDeploymentGroupKey(row);
    if (!acc[key]) acc[key] = [];
    acc[key].push(row);
    return acc;
  }, {});
  const groupedEntries = Object.entries(groupedRows).map(
    ([groupKey, items]) => ({
      groupKey,
      items: [...items].sort(
        (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
      ),
    }),
  );

  const statusColor = (s) =>
    ({
      SCHEDULED: "secondary",
      PENDING: "warning",
      IN_PROGRESS: "info",
      COMPLETED: "success",
      FAILED: "error",
      CANCELED: "default",
    })[s] || "default";

  const handleDeploymentCreated = async (deployments) => {
    const created = Array.isArray(deployments) ? deployments : [];

    await fetchDeployments();

    if (!created.length) {
      setDeploymentNotice("Deployment created successfully.");
      return;
    }

    setDeploymentNotice(
      `Deployment created for ${created.length} target device${created.length > 1 ? "s" : ""}. Immediate rollouts publish OTA_Update commands right away, while scheduled rollouts stay queued until their dispatch time and then move into the live progress flow.`,
    );

    if (created.length === 1 && created[0]?.id) {
      navigate(`/ota/deployments/${created[0].id}`);
    }
  };

  // reusable cell wrapper — vertically & horizontally centers content
  const Cell = ({ children, justify = "flex-start" }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: justify,
        width: "100%",
        height: "100%",
      }}
    >
      {children}
    </Box>
  );

  const columns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: "created_at",
      headerName: "Created At",
      flex: 1.2,
      renderCell: (p) => (
        <Tooltip title={p.value || ""} arrow>
          <Box sx={{ width: "100%" }}>
            <Cell>
              <AccessTime
                sx={{ fontSize: 13, color: "#94A3B8", mr: 0.6, flexShrink: 0 }}
              />
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#475569",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {p.value}
              </Typography>
            </Cell>
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "firmware_id",
      headerName: "Firmware ID",
      flex: 1.5,
      renderCell: (p) => (
        <Tooltip title={p.value || ""} arrow>
          <Box sx={{ width: "100%" }}>
            <Cell>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#1E40AF",
                  fontWeight: 600,
                  fontFamily: "monospace",
                  background: "#EFF6FF",
                  px: 0.8,
                  py: 0.3,
                  borderRadius: "5px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {(p.value || "").slice(0, 20)}…
              </Typography>
            </Cell>
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "device_name",
      headerName: "Device Name",
      flex: 1.3,
      renderCell: (p) => (
        <Tooltip title={p.value || "—"} arrow>
          <Box sx={{ width: "100%" }}>
            <Cell>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#0F172A",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {p.value || "—"}
              </Typography>
            </Cell>
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "target_id",
      headerName: "Device ID",
      flex: 1.6,
      renderCell: (p) => (
        <Tooltip title={p.value || ""} arrow>
          <Box sx={{ width: "100%" }}>
            <Cell>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#1E40AF",
                  fontWeight: 600,
                  fontFamily: "monospace",
                  background: "#EFF6FF",
                  px: 0.8,
                  py: 0.3,
                  borderRadius: "5px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {(p.value || "").slice(0, 20)}…
              </Typography>
            </Cell>
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "target_type",
      headerName: "Target",
      width: 120,
      renderCell: (p) => (
        <Tooltip title={p.value || ""} arrow>
          <Box sx={{ width: "100%" }}>
            <Cell>
              <Chip
                label={p.value}
                size="small"
                sx={{
                  fontSize: "10px",
                  fontWeight: 600,
                  background: "#F1F5F9",
                  color: "#475569",
                  height: 22,
                  borderRadius: "6px",
                }}
              />
            </Cell>
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "rollout_strategy",
      headerName: "Rollout",
      width: 140,
      renderCell: (p) => (
        <Tooltip
          title={p.value === "SCHEDULED" ? "Scheduled" : "Immediate"}
          arrow
        >
          <Box sx={{ width: "100%" }}>
            <Cell>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#64748B",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                }}
              >
                {p.value === "SCHEDULED" ? "Scheduled" : "Immediate"}
              </Typography>
            </Cell>
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      renderCell: (p) => {
        const effectiveStatus = getEffectiveDeploymentStatus(p.row);
        return (
          <Tooltip title={formatDeploymentStatusLabel(effectiveStatus)} arrow>
            <Box sx={{ width: "100%" }}>
              <Cell>
                <Chip
                  label={formatDeploymentStatusLabel(effectiveStatus)}
                  color={statusColor(effectiveStatus)}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "10px",
                    letterSpacing: "0.4px",
                    height: 22,
                    borderRadius: "6px",
                  }}
                />
              </Cell>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderCell: (p) => (
        <Tooltip title="Open deployment details" arrow>
          <Box sx={{ width: "100%" }}>
            <Cell justify="center">
              <Button
                size="small"
                variant="outlined"
                sx={{
                  fontSize: "10px",
                  fontWeight: 700,
                  borderRadius: "7px",
                  textTransform: "none",
                  py: 0.3,
                  px: 1.4,
                  minWidth: 0,
                  borderColor: "#E2E8F0",
                  color: "#475569",
                  "&:hover": {
                    borderColor: "#3B82F6",
                    color: "#3B82F6",
                    background: "#EFF6FF",
                  },
                }}
                onClick={() => navigate(`/ota/deployments/${p.row.id}`)}
              >
                View
              </Button>
            </Cell>
          </Box>
        </Tooltip>
      ),
    },
  ];
  const [visibleCount, setVisibleCount] = useState(10);

  const visibleEntries = groupedEntries.slice(0, visibleCount);
  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2.4 },
        mt: { xs: 5, md: 0 },
        background: "#EEF2F7",
        minHeight: "100vh",
        borderRadius: { xs: "14px", md: "22px" },
        border: "1px solid #E5EAF2",
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          mb: 2,
          px: { xs: 2, md: 3 },
          py: { xs: 3, md: 4 },
          borderRadius: 5,

          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",

          boxShadow: "0 30px 80px rgba(15, 23, 42, 0.25)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: { xs: 1.5, sm: 0 },
            mb: 2,
          }}
        >
          {/* Title */}
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "28px", md: "40px" },
                  color: "#fff",
                  letterSpacing: "-0.3px",
                  lineHeight: 1.1,
                  mb: 1,
                }}
              >
                Firmware Deployments
              </Typography>
              <Typography sx={{ fontSize: "11px", color: "#94A3B8" }}>
                OTA deployment operations
              </Typography>
            </Box>
          </Box>

          {/* Actions row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {/* Last updated — hidden on xs */}
            {lastUpdated && (
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#94A3B8",
                  display: { xs: "none", md: "flex" },
                  alignItems: "center",
                  gap: 0.4,
                }}
              >
                {/* <AccessTime sx={{ fontSize: 12 }} /> {timeAgo} */}
              </Typography>
            )}

            {/* Auto-refresh */}
            <Tooltip
              title={
                autoRefresh
                  ? `Auto-refresh ON — next in ${countdown}s`
                  : "Enable auto-refresh (30s)"
              }
            >
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                sx={{
                  background: autoRefresh ? "#EFF6FF" : "#F8FAFC",
                  border: "1.5px solid",
                  borderColor: autoRefresh ? "#BFDBFE" : "#E2E8F0",
                  borderRadius: "14px",
                  px: 1.2,
                  py: 0.4,
                  cursor: "pointer",
                }}
              >
                {autoRefresh ? (
                  <WifiTethering sx={{ fontSize: 14, color: "#3B82F6" }} />
                ) : (
                  <WifiTetheringOff sx={{ fontSize: 14, color: "#94A3B8" }} />
                )}
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: autoRefresh ? "#3B82F6" : "#94A3B8",
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  {autoRefresh ? `${countdown}s` : "Auto"}
                </Typography>
                <Switch
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  size="small"
                  sx={{
                    ml: 0.5,
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#3B82F6" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      background: "#93C5FD",
                    },
                  }}
                />
              </Box>
            </Tooltip>

            {/* Manual refresh */}
            <Tooltip title={`Refresh now${lastUpdated ? ` · ${timeAgo}` : ""}`}>
              <IconButton
                onClick={fetchDeployments}
                disabled={loading}
                sx={{
                  width: 34,
                  height: 34,
                  background: "#fff",
                  border: "1.5px solid #E2E8F0",
                  borderRadius: "14px",
                  "& svg": { fontSize: 16, color: "#475569" },
                  "&:hover": {
                    background: "#EFF6FF",
                    borderColor: "#BFDBFE",
                    "& svg": { color: "#3B82F6" },
                  },
                }}
              >
                <Refresh
                  sx={{
                    animation: loading ? "spin 1s linear infinite" : "none",
                    "@keyframes spin": {
                      from: { transform: "rotate(0deg)" },
                      to: { transform: "rotate(360deg)" },
                    },
                  }}
                />
              </IconButton>
            </Tooltip>

            {isPlatformAdmin && can(permissions, "firmware.deploy") && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setOpenCreate(true)}
                sx={{
                  px: 2.2,

                  py: 1.1,

                  borderRadius: 999,

                  textTransform: "none",

                  fontWeight: 700,

                  color: "#082F49",

                  background: "linear-gradient(135deg, #FDE68A, #F59E0B)",

                  boxShadow: "0 16px 30px rgba(245, 158, 11, 0.28)",
                }}
              >
                Create Deployment
              </Button>
            )}
          </Box>
        </Box>

        {/* ── Success Rate Banner ── */}
        <Alert severity="info" sx={{ mb: 2, borderRadius: "14px" }}>
          Immediate deployments send the frozen <strong>OTA_Update</strong> MQTT
          packet as soon as they are created. Scheduled deployments remain{" "}
          <strong>Scheduled</strong> until the backend scheduler dispatches
          them, then move to <strong>Dispatched</strong> and wait for device
          acknowledgements in this console.
        </Alert>

        {deploymentNotice && (
          <Alert
            severity="success"
            onClose={() => setDeploymentNotice("")}
            sx={{ mt: 1, borderRadius: "14px" }}
          >
            {deploymentNotice}
          </Alert>
        )}
      </Box>
      <SuccessRateBanner rows={rows} loading={loading} />

      {/* ── Top Section: Pie + Stats ── */}
      {can(permissions, "device.view") && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            mb: 2,
            alignItems: "flex-start",
          }}
        >
          {/* 3D Pie — full width on mobile */}
          <Card
            sx={{
              width: { xs: "100%", md: 300 },
              borderRadius: "16px",
              boxShadow: "0 3px 18px rgba(0,0,0,0.06)",
              border: "1px solid #F1F5F9",
              p: 2,
              background: "#fff",
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: { xs: 15, md: 20 },
                color: "#0F172A",
              }}
            >
              Status Breakdown
            </Typography>
            <Typography sx={{ fontSize: "11px", color: "#94A3B8", mb: 1 }}>
              Live distribution
            </Typography>
            <Divider sx={{ mb: 1.5 }} />

            <Box sx={{ perspective: "700px" }}>
              <Box
                sx={{
                  transform: "rotateX(36deg) scaleY(0.80)",
                  transformStyle: "preserve-3d",
                  filter: "drop-shadow(0px 18px 12px rgba(0,0,0,0.28))",
                  position: "relative",
                }}
              >
                {[...Array(12)].map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      position: "absolute",
                      top: `${i * 2}px`,
                      left: 0,
                      right: 0,
                      opacity: 0.12,
                      zIndex: -i,
                    }}
                  >
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={0}
                          outerRadius={75}
                          paddingAngle={1}
                          dataKey="value"
                          startAngle={90}
                          endAngle={-270}
                        >
                          {pieData.map((_, idx) => (
                            <PieCell
                              key={idx}
                              fill={PIE_DARK_COLORS[idx]}
                              stroke="none"
                            />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                ))}
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={0}
                      outerRadius={75}
                      paddingAngle={1}
                      dataKey="value"
                      startAngle={90}
                      endAngle={-270}
                      onMouseEnter={(_, i) => setActiveIndex(i)}
                      onMouseLeave={() => setActiveIndex(null)}
                    >
                      {pieData.map((_, idx) => (
                        <PieCell
                          key={idx}
                          fill={PIE_COLORS[idx]}
                          opacity={
                            activeIndex === null || activeIndex === idx
                              ? 1
                              : 0.45
                          }
                          stroke="#fff"
                          strokeWidth={1.5}
                        />
                      ))}
                    </Pie>
                    <ReTooltip
                      formatter={(v, n) => [v, n]}
                      contentStyle={{
                        borderRadius: 8,
                        border: "none",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                        fontSize: 11,
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Box>

            <Box
              sx={{
                width: "48%",
                height: "14px",
                background: "rgba(0,0,0,0.1)",
                borderRadius: "50%",
                filter: "blur(7px)",
                mx: "auto",
                mt: "-10px",
                mb: 1.5,
              }}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              {pieData.map((entry, idx) => (
                <Box
                  key={idx}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "2px",
                      bgcolor: PIE_COLORS[idx],
                    }}
                  />
                  <Typography
                    sx={{ fontSize: "10px", color: "#64748B", fontWeight: 500 }}
                  >
                    {entry.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Card>

          {/* Stats grid — 2 cols on mobile, 3 on desktop */}
          <Box
            sx={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(3, 1fr)",
              },
              gap: 1.5,
              width: { xs: "100%", md: "auto" },
            }}
          >
            {statsCards.map((card, i) => (
              <StatCard
                key={i}
                card={card}
                total={rows.length}
                delay={i * 60}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* ── Table Card ── */}
      <Card
        sx={{
          borderRadius: "22px",
          boxShadow: "0 3px 18px rgba(0,0,0,0.05)",
          border: "1px solid #F1F5F9",
          overflow: "hidden",
          background: "#FFFFFF",
        }}
      >
        {/* Table toolbar */}
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            pt: 1.5,
            pb: 1,
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",

            boxShadow: "0 30px 80px rgba(15, 23, 42, 0.25)",
            borderBottom: "1.5px solid #F1F5F9",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: 1,
              mb: 1,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: 15, md: 20 },
                  color: "#fff",
                }}
              >
                All Deployments
              </Typography>
              <Typography
                sx={{ fontSize: { xs: 10, md: 15 }, color: "#94A3B8" }}
              >
                Showing {filteredRows.length} of {rows.length} records
              </Typography>
            </Box>

            {/* Search — full width on mobile */}
            <TextField
              size="small"
              placeholder="Search firmware, target, status…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                width: { xs: "100%", sm: 360 },
                ml: "auto",
                borderRadius: 2,

                "& .MuiOutlinedInput-root": {
                  background: "rgba(255,255,255,0.08)",
                  color: "#fff",
                  borderRadius: 2,

                  // 🔥 DEFAULT BORDER
                  "& fieldset": {
                    borderColor: "rgba(255,255,255,0.3)",
                  },

                  // 🔥 HOVER BORDER (change this color)
                  "&:hover fieldset": {
                    borderColor: "#38BDF8", // light blue / or any color you want
                  },

                  // 🔥 FOCUSED BORDER (removes default blue)
                  "&.Mui-focused fieldset": {
                    borderColor: "#22C55E", // green (example)
                    borderWidth: "1px",
                  },
                },

                "& .MuiInputBase-input::placeholder": {
                  color: "rgba(255,255,255,0.5)",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 16, color: "#94A3B8" }} />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearch("");
                        setPaginationModel((prev) => ({ ...prev, page: 0 }));
                      }}
                      sx={{ color: "#fff" }}
                    >
                      ✕
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Status Tabs */}
          <Tabs
            value={tabStatus}
            onChange={(_, v) => setTabStatus(v)}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              minHeight: 32,
              "& .MuiTabs-indicator": {
                height: 2,
                borderRadius: 1,
                background: "#fff",
              },
              "& .MuiTab-root": {
                minHeight: 32,
                py: 0,
                px: 1.5,
                fontSize: "11px",
                fontWeight: 600,
                textTransform: "none",
                color: "#94A3B8",
                "&.Mui-selected": { color: "#fff" },
              },
            }}
          >
            {STATUS_TABS.map((s) => {
              const cnt = s === "ALL" ? rows.length : count(s);
              return (
                <Tab
                  key={s}
                  value={s}
                  label={
                    <Box display="flex" alignItems="center" gap={0.6}>
                      {formatTabLabel(s)}
                      <Box
                        sx={{
                          background: tabStatus === s ? "#fff" : "#F1F5F9",
                          color: tabStatus === s ? "#080808ff" : "#64748B",
                          borderRadius: "5px",
                          px: 0.6,
                          fontSize: { xs: 10, md: 13 },
                          fontWeight: 600,
                          lineHeight: "16px",
                          minWidth: 16,
                          textAlign: "center",
                        }}
                      >
                        {cnt}
                      </Box>
                    </Box>
                  }
                />
              );
            })}
          </Tabs>
        </Box>

        {/* DataGrid — horizontal scroll on mobile */}
        <Box sx={{ px: { xs: 0, sm: 1 }, overflowX: "auto" }}>
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "12px",
                color: "#64748B",
                mb: 1,
              }}
            >
              Grouped deployment attempts
            </Typography>
            {groupedEntries.length === 0 ? (
              <Typography sx={{ fontSize: 12, color: "#94A3B8" }}>
                No deployments found for the current filter.
              </Typography>
            ) : (
              <Box
                sx={{
                  maxHeight: {
                    xs: "60vh",
                    sm: "65vh",
                    md: "70vh",
                  },
                  overflowY: "auto",
                  pr: 1,
                  "&::-webkit-scrollbar": {
                    width: 6,
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#CBD5E1",
                    borderRadius: 3,
                  },
                }}
              >
                {visibleEntries.map((group) => {
                  const root = [...group.items].sort(
                    (a, b) =>
                      new Date(a.created_at || 0) - new Date(b.created_at || 0),
                  )[0];

                  const attempts = group.items;
                  const isExpanded = expandedGroups[group.groupKey] ?? true;

                  return (
                    <Card
                      key={group.groupKey}
                      sx={{
                        mb: 1.5,
                        borderRadius: 2,
                        border: "1px solid #E2E8F0",
                        boxShadow: "0 4px 12px rgba(15,23,42,.05)",
                      }}
                    >
                      <Box
                        sx={{
                          p: 2,
                          display: "flex",
                          flexDirection: {
                            xs: "column",
                            sm: "row",
                          },
                          justifyContent: "space-between",
                          alignItems: {
                            xs: "flex-start",
                            sm: "center",
                          },
                          gap: 1,
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          setExpandedGroups((prev) => ({
                            ...prev,
                            [group.groupKey]: !isExpanded,
                          }))
                        }
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: 13,
                              wordBreak: "break-word",
                            }}
                          >
                            {root.device_name || "Unnamed device"}

                            <Typography
                              component="span"
                              sx={{
                                ml: 1,
                                color: "#64748B",
                                fontWeight: 600,
                              }}
                            >
                              · {attempts.length} attempt
                              {attempts.length > 1 ? "s" : ""}
                            </Typography>
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: 11,
                              color: "#64748B",
                              wordBreak: "break-all",
                            }}
                          >
                            Device ID: {root.target_id || "—"}
                          </Typography>
                        </Box>

                        <Chip
                          label={isExpanded ? "Hide attempts" : "Show attempts"}
                          size="small"
                        />
                      </Box>

                      <Collapse in={isExpanded}>
                        <Box sx={{ px: 2, pb: 2 }}>
                          {attempts.map((row, idx) => (
                            <Box
                              key={row.id}
                              sx={{
                                display: "flex",
                                flexDirection: {
                                  xs: "column",
                                  sm: "row",
                                },
                                justifyContent: "space-between",
                                alignItems: {
                                  xs: "flex-start",
                                  sm: "center",
                                },
                                gap: 1,
                                py: 1.5,
                                borderTop: "1px solid #F1F5F9",
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                  }}
                                >
                                  Attempt #{idx + 1}
                                  {idx === 0
                                    ? " · Original deployment"
                                    : " · Redeploy"}
                                </Typography>

                                <Typography
                                  sx={{
                                    fontSize: 11,
                                    color: "#64748B",
                                    fontFamily: "monospace",
                                    wordBreak: "break-all",
                                  }}
                                >
                                  {row.id}
                                </Typography>
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  flexWrap: "wrap",
                                  width: {
                                    xs: "100%",
                                    sm: "auto",
                                  },
                                  justifyContent: {
                                    xs: "space-between",
                                    sm: "flex-end",
                                  },
                                }}
                              >
                                <Chip
                                  label={formatDeploymentStatusLabel(
                                    getEffectiveDeploymentStatus(row),
                                  )}
                                  color={statusColor(
                                    getEffectiveDeploymentStatus(row),
                                  )}
                                  size="small"
                                />

                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() =>
                                    navigate(`/ota/deployments/${row.id}`)
                                  }
                                >
                                  View
                                </Button>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Collapse>
                    </Card>
                  );
                })}

                {visibleCount < groupedEntries.length && (
                  <Box sx={{ textAlign: "center", py: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() => setVisibleCount((prev) => prev + 10)}
                    >
                      Load More
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {isPlatformAdmin && can(permissions, "firmware.view") ? (
            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <div style={{ minWidth: 1000 }}>
                <DataGrid
                  rows={filteredRows}
                  columns={columns}
                  density="compact"
                  loading={loading}
                  
                  pageSize={15}
                  rowsPerPageOptions={[15, 30, 50]}
                  getRowId={(r) => r.id}
                  disableColumnMenu
                  disableRowSelectionOnClick
                  sx={{
                    border: "none",
                    height:"500px",
                    overflow:"auto",
                    minWidth: 600,
                    "& .MuiDataGrid-columnHeaders": {
                      background: "#F8FAFC",
                      borderBottom: "1.5px solid #E2E8F0",
                      minHeight: "38px !important",
                      maxHeight: "38px !important",
                    },
                    "& .MuiDataGrid-columnHeader": {
                      height: "38px !important",
                      display: "flex",
                      alignItems: "center",
                      "&:focus, &:focus-within": { outline: "none" },
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontWeight: 700,
                      fontSize: "10px",
                      color: "#64748B",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      lineHeight: 1,
                    },
                    "& .MuiDataGrid-columnHeaderTitleContainer": {
                      display: "flex",
                      alignItems: "center",
                    },
                    "& .MuiDataGrid-row": {
                      "&:hover": { background: "#F0F7FF" },
                    },
                    "& .MuiDataGrid-cell": {
                      borderColor: "#F1F5F9",
                      display: "flex",
                      alignItems: "center",
                      fontSize: "12px",
                      color: "#334155",
                      "&:focus, &:focus-within": { outline: "none" },
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: "1.5px solid #E2E8F0",
                      background: "#FAFBFF",
                      minHeight: "42px",
                      display: "flex",
                      alignItems: "center",
                      "& .MuiTablePagination-root": { fontSize: "11px" },
                      "& .MuiTablePagination-toolbar": {
                        alignItems: "center",
                        minHeight: "42px",
                      },
                    },
                  }}
                />
              </div>
            </Box>
          ) : (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography sx={{ color: "#94A3B8", fontSize: "13px" }}>
                Firmware deployments are available only to platform
                administrators.
              </Typography>
            </Box>
          )}
        </Box>
      </Card>

      <CreateDeployment
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onSuccess={handleDeploymentCreated}
      />
    </Box>
  );
}